import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import StatusBadges from './StatusBadges';
import StudyIndexBadge from './StudyIndexBadge';
import { calculateAge, formatSexShort, combineUniqueValues } from '../../../utils/reportEditorUtils';
import { formatDateSync } from '../../../utils/dateTimeFormat';

export default function PatientDataCard({
  examData,
  combinedExams,
  isMultiExam,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isCombined = isMultiExam && combinedExams?.length > 1;

  const patientId = isCombined
    ? combineUniqueValues(combinedExams, 'patient_id') || examData?.patient_id || '-'
    : examData?.patient_id || '-';

  const accessionNumber = isCombined
    ? combineUniqueValues(combinedExams, 'accession_number') || examData?.accession_number || '-'
    : examData?.accession_number || '-';

  const admissionId = isCombined
    ? combineUniqueValues(combinedExams, 'admission_id') || examData?.admission_id || '-'
    : examData?.admission_id || '-';

  const birthDateRaw = examData?.birth_date;
  const birthDateDisplay = birthDateRaw ? formatDateSync(birthDateRaw) : '-';
  const age = calculateAge(birthDateRaw);
  const sexDisplay = formatSexShort(examData?.sex);

  return (
    <Paper elevation={1} sx={{ flexShrink: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.3 }}>
            {examData?.patient_name || '-'}
          </Typography>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? 'collapse' : 'expand'}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: expanded ? 1.5 : 0 }}>
          <Typography variant="body2" color="text.secondary">
            {birthDateDisplay}
            {age != null && ` · ${age}Y`}
            {sexDisplay !== '-' && ` · ${sexDisplay}`}
          </Typography>
          <StatusBadges
            insuranceStatus={examData?.insurance_status}
            emergencyStatus={examData?.emergency_status}
          />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                {t('report.patientId')}:
              </Typography>
              <Typography variant="body2">{patientId}</Typography>
            </Box>
            {isCombined ? (
              combinedExams.map((exam, index) => (
                <Box
                  key={exam.id || exam.new_exam_id || index}
                  sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25, minWidth: 0 }}
                >
                  <StudyIndexBadge index={index + 1} />
                  <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {t('report.accessionNumber', 'Accession Number')}:
                    </Typography>
                    {' '}
                    {exam.accession_number || '-'}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ mx: 0.75 }}>
                      —
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {t('report.caseNumber')}:
                    </Typography>
                    {' '}
                    {exam.admission_id || '-'}
                  </Typography>
                </Box>
              ))
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                    {t('report.accessionNumber', 'Accession Number')}:
                  </Typography>
                  <Typography variant="body2">{accessionNumber}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                    {t('report.caseNumber')}:
                  </Typography>
                  <Typography variant="body2">{admissionId}</Typography>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
}
